import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Box, Button, TextField, Typography, Paper, Alert, CircularProgress,
} from "@mui/material";
import { auth } from "../api/endpoints";
import { useLanguage } from "../context/LanguageContext";
import AuthShell from "../components/AuthShell";
import { alpha } from "@mui/material/styles";

const NAVY    = "#0F2156";
const CRIMSON = "#B91C2C";
const GOLD    = "#E8A020";

/** Translate raw backend validation errors into user-friendly messages. */
function translateResetError(message: string, t: (k: string) => string): string {
  const lower = message.toLowerCase();
  if (lower.includes("email_or_username") || lower.includes("emailorusername")) {
    return t("auth.emailOrUsernameRequired");
  }
  if (lower.includes("code") && lower.includes("required")) {
    return t("auth.resetCodeRequired");
  }
  if (lower.includes("password") && (lower.includes("least") || lower.includes("short") || lower.includes("min"))) {
    return t("auth.passwordTooShort");
  }
  if (lower.includes("password") && (lower.includes("most") || lower.includes("long") || lower.includes("max"))) {
    return t("auth.passwordTooLong");
  }
  if (lower.includes("invalid") && lower.includes("code")) {
    return t("auth.invalidResetCode");
  }
  return message;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const state = location.state as { emailOrUsername?: string; devCode?: string } | null;

  const [emailOrUsername, setEmailOrUsername] = useState(state?.emailOrUsername ?? "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate top-to-bottom, stop at first error
    if (!emailOrUsername.trim()) { setError(t("auth.emailOrUsernameRequired")); return; }
    if (!code.trim())            { setError(t("auth.resetCodeRequired")); return; }
    if (newPassword.length < 6)  { setError(t("auth.passwordTooShort")); return; }
    if (newPassword.length > 30) { setError(t("auth.passwordTooLong")); return; }
    if (!confirmPassword)        { setError(t("auth.passwordRequired")); return; }
    if (newPassword !== confirmPassword) { setError(t("auth.passwordsDoNotMatch")); return; }

    setLoading(true);
    setError(null);
    try {
      await auth.resetPassword({ email_or_username: emailOrUsername, code, new_password: newPassword });
      navigate("/login", { state: { successMessage: t("auth.passwordResetSuccess") } });
    } catch (err) {
      setError(err instanceof Error ? translateResetError(err.message, t) : t("auth.resetPassword"));
    } finally {
      setLoading(false);
    }
  };

  const headerRight = (
    <Button
      variant="outlined"
      size="small"
      onClick={() => navigate("/login")}
      sx={{
        color: "white",
        borderColor: alpha("#fff", 0.4),
        fontWeight: 600,
        textTransform: "none",
        fontSize: "0.82rem",
        "&:hover": { borderColor: "white", bgcolor: alpha("#fff", 0.08) },
      }}
    >
      {t("auth.login")}
    </Button>
  );

  return (
    <AuthShell rightContent={headerRight}>
      <Box
        sx={{
          minHeight: "calc(100vh - 56px - 60px)",
          background: `linear-gradient(145deg, ${NAVY} 0%, #1a3a7a 45%, ${alpha(CRIMSON, 0.85)} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: alpha(GOLD, 0.1),
            filter: "blur(80px)",
            pointerEvents: "none",
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 420,
            p: { xs: 3, sm: 4 },
            borderRadius: "20px",
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
            position: "relative", zIndex: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: 3,
              borderRadius: "20px 20px 0 0",
              background: `linear-gradient(90deg, ${CRIMSON} 0%, ${NAVY} 100%)`,
            },
          }}
        >
          <Typography variant="h5" fontWeight={800} mb={0.75} sx={{ color: "#1A1A2E", letterSpacing: "-0.02em" }}>
            {t("auth.resetPassword")}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5} sx={{ lineHeight: 1.6 }}>
            {t("auth.resetPasswordDesc")}
          </Typography>

          {state?.devCode && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              {t("auth.devCode")}: <strong>{state.devCode}</strong>
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label={t("auth.emailOrUsername")}
              fullWidth margin="normal"
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              placeholder={t("auth.emailOrUsernamePlaceholder")}
              required
            />
            <TextField
              label={t("auth.resetCode")}
              fullWidth margin="normal"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputProps={{ maxLength: 6 }}
              required
            />
            <TextField
              label={t("auth.newPassword")}
              type="password" fullWidth margin="normal"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              inputProps={{ maxLength: 30 }}
            />
            <TextField
              label={t("auth.confirmPassword")}
              type="password" fullWidth margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 2.5, py: 1.4, borderRadius: 12, fontWeight: 700 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : t("auth.resetPassword")}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography
              variant="body2"
              component={Link}
              to="/login"
              sx={{ textDecoration: "none", color: "#9CA3AF", fontSize: "0.82rem", "&:hover": { color: CRIMSON } }}
            >
              {t("auth.backToLogin")}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </AuthShell>
  );
}
